import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import BaseTable from 'components/shared/BaseTable';
import {
  headerOperation,
  cellNumOperation,
  cellOperation,
  cellDobleRowOperation,
  headerDoubleRowOperation,
} from 'helpers/tables';
import { get } from 'helpers/requests';
import {
  BASE_URL,
  API,
  CONSTRUCTION_LABEL,
  DRAFT_LABEL,
  CANCELED_LABEL,
  EMITTED_LABEL,
  FRONT_DF_1,
} from 'CONST';
import { fdate } from 'helpers/helpers';
import FilterSelect from 'components/shared/FilterSelect';
import useTableFilters from 'components/shared/useTableFilters';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import DateTableFilter from '../shared/DateTableFilter';
import TableLink from '../shared/TableLink';
import DownloadBtn from '../shared/DownloadBtn';

const NO_FILTERED_ID = -1;
const returnToUrl = '/resumen';

const Resumen = ({ client, user }) => {
  const [ramosOpts, setRamosOpts] = useState([])
  const [tiposIntermediarioOpts, setTiposIntermediarioOpts] = useState([])
  const [monedasOpts, setMonedasOpts] = useState([])
  const [onFilteredChange, onFetchData, loading, data] = useTableFilters(
    `${BASE_URL}${API.fullPolizas}`,
    false,
    client ? `client_id=${client.id}` : '',
    [
      { id: 'inicio_vigencia', format: FRONT_DF_1 },
      { id: 'termino_vigencia', format: FRONT_DF_1 },
    ]
  );
  const tableRef = useRef(null);

  useEffect(() => {
    if (client) {
      onFetchData(tableRef.current.state);
    }
  }, [client]);

  const filteredData = data.data.filter(row => {
    const tieneCredito = user?.roles.includes('aseguradocredito');
    const tieneGarantia = user?.roles.includes('aseguradogarantia');
    if (tieneCredito && user.is_orsan === false) {

      // Solo mostrar datos de Crédito si tiene el rol 'aseguradocredito' y no 'aseguradogarantia'
      return row.ramo && row.ramo.includes('Crédito');

    } else if (tieneGarantia && !tieneCredito && user.is_orsan === false) {
      // Solo mostrar datos de Garantía si tiene el rol 'aseguradogarantia' y no 'aseguradocredito'
      return row.ramo && row.ramo.includes('Garantía');
    }
    // Si tiene ambos roles o ninguno, mostrar todos los datos (esto hay que revisarlo...)
    return true;


    // Si tiene ambos roles, mostrar solo crédito por solicitud...
    // return row.ramo && row.ramo.includes('Crédito');
  });



  useEffect(() => {
    // Get ramos
    get(`${BASE_URL}${API.ramosFecu}/`)
      .then((resp) => {
        const ramos = resp.map(elem => ({
          label: elem.descripcion,
          value: elem.id
        }));
        const filteredRamos = ramos.filter(option => {
          const tieneCredito = user?.roles.includes('aseguradocredito');
          const tieneGarantia = user?.roles.includes('aseguradogarantia');
  
          if (tieneCredito && !tieneGarantia) {
            return option.label.includes('Crédito');
          } else if (tieneGarantia && !tieneCredito) {
            return option.label.includes('Garantía');
          }
          // Si tiene ambos roles o ninguno, mantener todas las opciones
          return true;
        });
  
        setRamosOpts(filteredRamos);
      });
  
    // Get tipos intermediario
    get(`${BASE_URL}${API.tiposIntermediario}/`)
      .then((resp) => setTiposIntermediarioOpts(resp.map(elem => ({
        label: elem.descripcion,
        value: elem.id
      }))));
  
    // Get monedas
    get(`${BASE_URL}${API.monedas}/`)
      .then((resp) => setMonedasOpts(resp.map(elem => ({
        label: elem.nombre_corto,
        value: elem.id
      }))));
  }, [user?.roles]);

  const xlsxUrl = `${BASE_URL}${API.fullPolizas}/download_xlsx/`;
  const finalXlsxUrl = client ? `${xlsxUrl}?client_id=${client.id}` : xlsxUrl;
  const pdfUrl = `${BASE_URL}${API.fullPolizas}/download_pdf/`;
  const finalPdfUrl = client ? `${pdfUrl}?client_id=${client.id}` : pdfUrl;

  const columns = [
    {
      Header: headerOperation('Ramo'),
      id: 'poliza_maestra__ramo_asegurado_id',
      accessor: 'ramo',
      Cell: (row) => cellOperation(row.value),
      Filter: obj => (
        <FilterSelect
          filter={obj.filter}
          defaultOption={{ label: 'Todos', value: -1 }}
          onFilterChange={obj.onChange}
          options={ramosOpts}
        />
      ),
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Número de Póliza'),
      id: 'nemotecnico__icontains',
      accessor: 'nemotecnico',
      Cell: (row) => cellOperation(row.value),
      sortable: false,
      minWidth: 100,
    },
    // {
    //   Header: headerOperation('Afianzado'),
    //   id: 'garantia__afianzado',
    //   accessor: (row) =>
    //     row.credito
    //       ? '-'
    //       : cellDobleRowOperation(
    //           row.garantia.afianzado.rut,
    //           row.garantia.afianzado.nombre,
    //         ),
    //   sortable: false,
    //   minWidth: 120,
    // },
    {
      Header: headerOperation('Asegurado'),
      id: 'asegurado',
      accessor: 'asegurado',
      Cell: (row) => cellDobleRowOperation(row.value.rut, row.value.nombre),
      sortable: false,
      minWidth: 120,
    },
    {
      Header: headerDoubleRowOperation('Tipo', 'Intermediario'),
      id: 'tipo_intermediario_id',
      accessor: 'tipo_intermediario.descripcion',
      Cell: (row) => cellOperation(row.value),
      Filter: obj => (
        <FilterSelect
          filter={obj.filter}
          defaultOption={{ label: 'Todos', value: -1 }}
          onFilterChange={obj.onChange}
          options={tiposIntermediarioOpts}
        />
      ),
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation('Intermediario'),
      id: 'intermediario',
      accessor: (row) =>
        row.intermediario
          ? cellDobleRowOperation(row.intermediario.rut, row.intermediario.nombre)
          : '-',
      sortable: false,
      minWidth: 120,
    },
    {
      Header: headerOperation('Moneda'),
      id: 'moneda_asegurada_id',
      accessor: 'moneda',
      Cell: (row) => cellOperation(row.value),
      Filter: obj => (
        <FilterSelect
          onFilterChange={obj.onChange}
          filter={obj.filter}
          options={monedasOpts}
          defaultOption={{ label: 'Todas', value: NO_FILTERED_ID }}
        />
      ),
      sortable: false,
      minWidth: 70,
    },
    {
      Header: headerOperation('Prima Neta'),
      id: 'prima_neta',
      accessor: 'prima_neta',
      Cell: (row) => cellNumOperation(row.value, 'float'),
      filterable: false,
      sortable: false,
      minWidth: 80,
    },
    {
      Header: headerDoubleRowOperation('Inicio', 'Cobertura '),
      id: 'inicio_vigencia',
      accessor: 'inicio_vigencia',
      Cell: (row) => cellOperation(fdate(row.value)),
      // eslint-disable-next-line react/prop-types
      Filter: ({ filter, onChange }) => (
        <DateTableFilter filter={filter} onChange={onChange} format={FRONT_DF_1} />
      ),
      sortable: false,
      minWidth: 80,
    },
    {
      Header: headerDoubleRowOperation('Fin', 'Cobertura'),
      id: 'termino_vigencia',
      accessor: 'termino_vigencia',
      Cell: (row) => cellOperation(fdate(row.value)),
      // eslint-disable-next-line react/prop-types
      Filter: ({ filter, onChange }) => (
        <DateTableFilter filter={filter} onChange={onChange} format={FRONT_DF_1} />
      ),
      sortable: false,
      minWidth: 80,
    },
    {
      Header: headerOperation('Vigente'),
      id: 'vigente',
      accessor: 'vigente',
      Cell: (row) => cellOperation(row.value ? 'Sí' : 'No'),
      Filter: (obj) => (
        <FilterSelect
          onFilterChange={obj.onChange}
          filter={obj.filter}
          options={[{ label: 'Sí', value: 'true' }, { label: 'No', value: 'false' }]}
          defaultOption={{ label: 'Todas', value: null }}
        />
      ),
      minWidth: 80,
      sortable: false,
    },
    {
      Header: headerOperation('Estado'),
      id: 'estado',
      accessor: 'estado',
      Cell: (row) => {
        let alertClass = 'alert-success';
        const estado = row.value;

        if (estado === DRAFT_LABEL) {
          alertClass = 'alert-primary';
        } else if (estado === CONSTRUCTION_LABEL) {
          alertClass = 'alert-warning';
        } else if (estado === CANCELED_LABEL) {
          alertClass = 'alert-danger';
        }
        return cellOperation(
          <div className={`alert ${alertClass} text-center`} role="alert">
            {estado}
          </div>,
        );
      },
      Filter: (obj) => (
        <FilterSelect
          onFilterChange={obj.onChange}
          filter={obj.filter}
          options={[
            { label: EMITTED_LABEL, value: 'emitida' },
            { label: DRAFT_LABEL, value: 'borrador' },
            { label: CONSTRUCTION_LABEL, value: 'construccion' },
            { label: CANCELED_LABEL, value: 'cancelada' },
          ]}
          defaultOption={{ label: 'Todos', value: NO_FILTERED_ID }}
        />
      ),
      minWidth: 120,
      sortable: false,
    },
    {
      Header: headerOperation('Acciones'),
      id: 'actions',
      Cell: (row) => cellOperation(row.original.credito ? (
        <TableLink
          title="ver"
          icon={faEye}
          url={`/asegurado/polizas-credito/${row.original.credito.id}`}
          disabled={!['Emitida', 'Borrador'].includes(row.original.estado)}
          returnTo={returnToUrl}
        />
      ) : (
        <TableLink
          title="ver"
          icon={faEye}
          url={`/asegurado/polizas-garantia/${row.original.garantia.id}`}
          disabled={!['Emitida', 'Borrador'].includes(row.original.estado)}
          returnTo={returnToUrl}
        />
      )),
      filterable: false,
      sortable: false,
      width: 100,
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gridGap: '10px',
          marginTop: '15px',
          marginBottom: '6px',
        }}
      >
        <div style={{ gridColumnStart: 2 }}>
          <DownloadBtn
            id="xlsx-download"
            text="XLSX"
            tipText="Descargar Planilla Excel"
            url={finalXlsxUrl}
          />
        </div>
        <div style={{ gridColumnStart: 3 }}>
          <DownloadBtn
            id="pdf-download"
            text="PDF"
            tipText="Descargar PDF"
            url={finalPdfUrl}
          />
        </div>
      </div>
      <BaseTable
        tableRef={tableRef}
        columns={columns}
        // data={data.data}
        data = {filteredData}
        showPagination
        pages={data.pages}
        loading={loading}
        resultCount={data.count}
        onFetchData={onFetchData}
        onFilteredChange={onFilteredChange}
        style={{ maxHeight: '63vh' }}
        filterable
      />
    </>
  );
};

Resumen.propTypes = {
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
};

Resumen.defaultProps = {
  client: null,
};

export default Resumen;
